import s from './Step.module.css';
import type { OrderItem } from '../../graphql/query/getOrder';
import { Flex } from '@repo/ui/Flex';
import { Task } from '../tasks/Task/Task';
import { taskComponentMap } from '../tasks/taskComponent';
import type { BaseSyntheticEvent } from 'react';
import { useFormContext } from 'react-hook-form';
import { useWorkflow } from '../../workflow/hooks';
import type { Tasks } from '../../workflow/schemaTypes';
import type { WorkflowItemData } from '../../workflow';
import { ItemCard, type StatusBadge } from '../ItemCard/ItemCard';
import { useIsMobile } from '@repo/lib/hooks/useMediaQuery';
import { TaskPreview } from '../tasks/TaskPreview/TaskPreview';
import { rawString } from '../Language/Language';
import { useSafePermissions } from '../../providers/permissions/usePermissions';

export interface StepItemProps {
  orderItem?: OrderItem;
  itemId: string;
  group: {
    id: string;
    scope: string;
    mode: string;
    tasks: Array<{ id: Tasks }>;
  };
}

export const StepItem = ({ orderItem, itemId, group }: StepItemProps) => {
  const { setValue } = useFormContext();
  const { updateData, data, prev } = useWorkflow();
  const isMobile = useIsMobile();
  const permissions = useSafePermissions();

  const canOverride = permissions?.hasPermission('item.override') ?? false;

  const itemData = data?.items?.[itemId];
  const isOverridden = itemData?.itemSelection?.isOverridden === true;

  const getStatusBadges = (): StatusBadge[] | undefined => {
    if (!isOverridden) {
      return undefined;
    }

    const ineligibleLabel = rawString({
      langKey: 'pdx.item_selection.ineligible_badge',
    });
    const overrideLabel = canOverride
      ? rawString({ langKey: 'pdx.item_selection.badge.override_available' })
      : null;

    const badges: StatusBadge[] = [];

    if (ineligibleLabel) {
      badges.push({ label: ineligibleLabel, variant: 'ineligible' });
    }

    if (overrideLabel) {
      badges.push({ label: overrideLabel, variant: 'override' });
    }

    return badges.length > 0 ? badges : undefined;
  };

  const handleQuantitySelect = (value: string) => {
    if (!data?.items) {
      return;
    }

    const updatedItems = Object.entries(data.items).reduce<
      Record<string, WorkflowItemData>
    >((acc, [id, itemData]) => {
      if (id === itemId) {
        return {
          ...acc,
          [id]: {
            ...itemData,
            itemSelection: {
              id,
              quantity: parseInt(value),
            },
            resolution: {
              resolutionId: '',
            },
          },
        };
      }
      return {
        ...acc,
        [id]: itemData,
      };
    }, {});

    setValue('items', updatedItems);
    setValue('returnMethod', { type: undefined });
    const updatedData = {
      ...data,
      items: updatedItems,
      returnMethod: { type: undefined },
    };
    updateData(updatedData);
  };

  const handleRemoveItem = () => {
    if (!data?.items) {
      return;
    }

    const updatedItems = Object.entries(data.items).reduce<
      Record<string, WorkflowItemData>
    >((acc, [id, itemData]) => {
      if (id === itemId) return acc;
      return {
        ...acc,
        [id]: itemData,
      };
    }, {});

    setValue('items', updatedItems);
    setValue('returnMethod', { type: undefined });

    updateData({
      ...data,
      items: updatedItems,
      returnMethod: { type: undefined },
    });

    if (Object.keys(data?.items ?? {}).length === 1) {
      prev();
    }
  };

  const handleNext = async (e?: BaseSyntheticEvent) => {
    e?.preventDefault();
    // This will be handled by the parent form's onSubmit
    return Promise.resolve();
  };

  return (
    <ItemCard
      item={orderItem}
      imageSize={isMobile ? 'md' : 'xl'}
      itemId={itemId}
      onQuantityChange={handleQuantitySelect}
      onRemove={handleRemoveItem}
      collapsible={true}
      defaultCollapsed={false}
      borderRadius="xl"
      data-testid="reasonsCard"
      enableQuantityAutoSetting={true}
      statusBadges={getStatusBadges()}
    >
      <Flex direction="column" className={s.itemTasks}>
        {group.tasks.map((task) => {
          const { id } = task;
          const TaskComponent = taskComponentMap[id];

          return (
            <Task key={id} itemId={itemId} taskId={id}>
              <TaskComponent handleNext={handleNext} itemId={itemId} />
            </Task>
          );
        })}
        <TaskPreview itemId={itemId} group={group} />
      </Flex>
    </ItemCard>
  );
};
